import React, { useState, useEffect, useRef, useId } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Link } from './SlashLink';
import { DecorLines } from './DecorLines';
import {
  Shield,
  Sparkles,
  Mail,
  Heart,
  ArrowUp,
  Search,
  Share2,
  Check,
  GraduationCap,
  Calculator,
  Target,
  BookOpen,
  Percent,
  Lightbulb,
  Banknote,
  House,
  KeyRound,
  X,
  AlertTriangle,
  Send,
  CheckCircle2,
  FileText,
  Loader2,
  MessageSquare,
  Bug,
  PlusCircle,
  ArrowRight,
  ChevronDown,
} from 'lucide-react';
import { getToolKeyFromPath, CONTACT_EMAIL, TOOL_PATHS } from '../data/constants';
import { NAV_ITEMS } from '../data/navItems';
import { CGPA_UNIVERSITY_PAGES } from '../data/cgpaUniversityPages';
import { isSamePath } from '../utils/paths';
import { ToolKey } from '../types';
import { preloadTool } from '../utils/toolPreloader';
import { SITE_URL } from '../data/seoConfig';
import { submitForm } from '../utils/formSubmit';
import { Logo } from './Logo';

export interface FooterProps {
  activeTool?: ToolKey;
  onSelectTool?: (tool: ToolKey) => void;
  onOpenCommandPalette?: () => void;
  setToast?: (msg: string) => void;
}


const FOOTER_PREMIUM_CSS = `
/* Light newsletter band: pale aurora wash, white glass controls, brand-blue action. No dark surfaces. */
.ft-news{position:relative;isolation:isolate;overflow:hidden;color:#0E1424;
  background:radial-gradient(60% 90% at 8% 0%,#E6EDFF 0%,rgba(230,237,255,0) 70%),radial-gradient(50% 80% at 100% 100%,#FFEFE3 0%,rgba(255,239,227,0) 70%),radial-gradient(40% 70% at 70% 0%,#EFE8FF 0%,rgba(239,232,255,0) 70%),#FFFFFF;
  animation:ft-rise .7s cubic-bezier(.2,.8,.2,1) both;
  border:1px solid #E3E8F8;box-shadow:0 30px 70px -36px rgba(36,87,245,.35),0 1px 0 rgba(255,255,255,.9) inset}
.ft-news::before{content:"";position:absolute;inset:0;z-index:-1;opacity:.5;
  background-image:linear-gradient(rgba(36,87,245,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(36,87,245,.06) 1px,transparent 1px);
  background-size:34px 34px;-webkit-mask-image:radial-gradient(ellipse at 30% 20%,#000 0%,transparent 70%);mask-image:radial-gradient(ellipse at 30% 20%,#000 0%,transparent 70%)}
.ft-orb{position:absolute;z-index:-1;border-radius:9999px;filter:blur(54px);pointer-events:none}
.ft-orb-a{width:15rem;height:15rem;left:-4rem;top:-5rem;background:rgba(36,87,245,.16);animation:ft-drift 11s ease-in-out infinite}
.ft-orb-b{width:17rem;height:17rem;right:-5rem;bottom:-7rem;background:rgba(255,160,102,.2);animation:ft-drift 14s ease-in-out infinite reverse}
.ft-title-accent{background:linear-gradient(90deg,#2457F5,#6F5BFF 70%);-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-fill-color:transparent}
.ft-float{animation:ft-float 6s ease-in-out infinite}
.ft-spark{transform-box:fill-box;transform-origin:center;animation:ft-twinkle 2.8s ease-in-out infinite}
.ft-spark:nth-of-type(2){animation-delay:.9s}.ft-spark:nth-of-type(3){animation-delay:1.7s}
.ft-chip{display:inline-flex;align-items:center;gap:.4rem;padding:.3rem .75rem;border-radius:9999px;font-size:.72rem;font-weight:600;color:#1B2340;
  background:rgba(255,255,255,.85);border:1px solid #E1E7FB;-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px)}
.ft-form{display:flex;align-items:center;gap:.4rem;padding:.4rem;border-radius:9999px;background:#fff;border:1px solid #DDE4F8;
  box-shadow:0 8px 24px -16px rgba(36,87,245,.35);transition:box-shadow .25s,border-color .25s}
.ft-form:focus-within{border-color:#2457F5;box-shadow:0 0 0 4px rgba(36,87,245,.16),0 12px 30px -14px rgba(36,87,245,.45)}
.ft-btn{position:relative;overflow:hidden;display:inline-flex;align-items:center;justify-content:center;gap:.5rem;min-height:48px;padding:0 1.35rem;border:0;border-radius:9999px;cursor:pointer;flex-shrink:0;
  font-weight:700;font-size:.85rem;color:#fff;background:linear-gradient(135deg,#2457F5 0%,#4F7BFF 100%);
  box-shadow:0 10px 24px -10px rgba(36,87,245,.65),inset 0 1px 0 rgba(255,255,255,.25);transition:translate .3s cubic-bezier(.32,.72,0,1),scale .38s cubic-bezier(.34,1.56,.64,1),box-shadow .3s ease}
.ft-btn::after{content:"";position:absolute;top:0;left:-70%;width:45%;height:100%;transform:skewX(-20deg);
  background:linear-gradient(90deg,transparent,rgba(255,255,255,.6),transparent);animation:ft-shine 5s ease-in-out infinite}
.ft-btn:hover:not(:disabled){translate:0 -1.5px;box-shadow:0 16px 34px -10px rgba(36,87,245,.75),0 0 0 4px rgba(36,87,245,.14),inset 0 1px 0 rgba(255,255,255,.3)}
.ft-btn:active:not(:disabled){scale:.96;translate:0 0;transition-duration:.1s,.1s,.2s}
.ft-btn:disabled{opacity:.75;cursor:wait}
.ft-btn:focus-visible{outline:3px solid #2457F5;outline-offset:2px}
.ft-btn .ft-arrow{transition:transform .2s}.ft-btn:hover:not(:disabled) .ft-arrow{transform:translateX(3px)}
.ft-msg{animation:ft-pop .35s cubic-bezier(.2,1.2,.3,1) both}
.ft-more-btn{display:inline-flex;align-items:center;gap:.35rem;min-height:36px;padding:.3rem .85rem;border-radius:9999px;cursor:pointer;
  font-size:12px;font-weight:700;color:#1A41C9;background:#EEF2FF;border:1px solid #D5DEFF;transition:background .2s,color .2s,transform .2s,box-shadow .2s}
.ft-more-btn:hover{background:#2457F5;color:#fff;border-color:#2457F5;transform:translateY(-1px);box-shadow:0 8px 18px -10px rgba(36,87,245,.9)}
.ft-more-btn:focus-visible{outline:3px solid #2457F5;outline-offset:2px}
.ft-more-chev{transition:transform .25s}
.ft-more-in{animation:ft-reveal .28s cubic-bezier(.2,.8,.2,1) both}
@keyframes ft-rise{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
@keyframes ft-drift{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(28px,18px) scale(1.12)}}
@keyframes ft-float{0%,100%{transform:translateY(0) rotate(-1deg)}50%{transform:translateY(-10px) rotate(1.5deg)}}
@keyframes ft-twinkle{0%,100%{opacity:.35;transform:scale(.8)}50%{opacity:1;transform:scale(1.15)}}
@keyframes ft-shine{0%,60%{left:-70%}100%{left:130%}}
@keyframes ft-pop{from{opacity:0;transform:translateY(6px) scale(.94)}to{opacity:1;transform:none}}
@keyframes ft-reveal{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){
  .ft-btn:hover:not(:disabled){translate:none}
  .ft-news,.ft-orb,.ft-float,.ft-spark,.ft-btn::after,.ft-msg,.ft-more-in{animation:none!important}
  .ft-more-btn,.ft-more-chev,.ft-btn,.ft-btn .ft-arrow{transition:none!important}
}
`;

/**
 * Pages that are not in the top navigation but must stay one click from every page (and from the homepage),
 * which the launch audit (CHK-16) enforces for every sitemap URL.
 */
/** Links shown before the "More" button in each footer column (all columns line up at this height). */
const FOOTER_VISIBLE_LINKS = 4;

const FOOTER_EXTRA_ACADEMIC_LINKS: { label: string; title: string; path: string }[] = [
  { label: 'Grading Scales', title: 'Grading scale charts by number of questions', path: '/grading-scale/' },
  { label: 'Weighted GPA', title: 'Weighted GPA calculator', path: '/weighted-gpa-calculator/' },
  { label: 'Weighted vs Unweighted GPA', title: 'Weighted vs unweighted GPA explained', path: '/weighted-vs-unweighted-gpa/' },
  { label: 'How Many Can I Miss', title: 'How many questions can you miss calculator', path: '/how-many-questions-can-i-miss/' },
  { label: 'Semester GPA', title: 'Semester GPA calculator', path: '/semester-gpa-calculator/' },
  { label: 'Cumulative GPA', title: 'Cumulative GPA calculator', path: '/cumulative-gpa-calculator/' },
  // Competitor-gap pages (2026-10-28): footer links keep every new page one
  // click from the homepage, which the launch audit (CHK-16) enforces.
  { label: 'Target GPA Calculator', title: 'Target GPA calculator — plan the grades you need', path: '/target-gpa-calculator/' },
  { label: 'Quiz Grade Calculator', title: 'Quiz grade calculator — score, percent and letter grade', path: '/quiz-grade-calculator/' },
  { label: 'Assignment Grade Calculator', title: 'Assignment grade calculator — late and extra credit', path: '/assignment-grade-calculator/' },
  { label: 'Points Grade Calculator', title: 'Points grade calculator — points to percent and letter', path: '/points-grade-calculator/' },
  { label: 'Category Grade Calculator', title: 'Category grade calculator — weighted course grades', path: '/category-grade-calculator/' },
  { label: 'GPA Converter', title: 'GPA converter — SGPA, CGPA and percent in one place', path: '/gpa-converter/' },
  { label: 'For Schools', title: 'Embed free grade calculators on your school website', path: '/for-schools/' },
  { label: 'Blog', title: 'Grading guides and articles', path: '/blog/' },
  { label: 'Grade 30 Tests in 15 Minutes', title: 'How to grade a stack of 30 tests in 15 minutes', path: '/blog/how-to-grade-30-tests-in-15-minutes/' },
  { label: 'When to Curve Grades', title: 'When to curve grades: a teacher’s guide', path: '/blog/when-to-curve-grades/' },
  { label: 'Grading Systems Explained', title: 'Grading systems explained: A–F, pass/fail, standards-based and more', path: '/blog/grading-systems-explained/' },
  { label: 'High School vs College GPA', title: 'High school GPA vs college GPA: what actually changes', path: '/blog/high-school-vs-college-gpa/' },
  // One link per verified university CGPA page, generated from the same data
  // as the pages so the footer can never drift out of sync with them.
  ...CGPA_UNIVERSITY_PAGES.map((u) => ({
    label: `${u.name.split(' (')[0]} CGPA to %`,
    title: `${u.name} CGPA to percentage`,
    path: `/cgpa-to-percentage/${u.id}/`,
  })),
];

export const Footer: React.FC<FooterProps> = ({
  activeTool: propActiveTool,
  onSelectTool,
  onOpenCommandPalette,
  setToast,
}) => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  const currentToolKey = propActiveTool || getToolKeyFromPath(location.pathname);

  // States for interactive modals
  const [activeModal, setActiveModal] = useState<
    'contact' | 'reportIssue' | 'suggestFeature' | null
  >(null);

  // Share & copy states
  const [copiedEmail, setCopiedEmail] = useState<boolean>(false);
  const [copiedLink, setCopiedLink] = useState<boolean>(false);

  // Newsletter subscribe form state
  const [subscribeEmail, setSubscribeEmail] = useState<string>('');
  const [subscribeLoading, setSubscribeLoading] = useState<boolean>(false);
  const [subscribeMessage, setSubscribeMessage] = useState<{ text: string; isError?: boolean } | null>(null);
  // Long footer link columns show a few links first and expand with the "More" button
  // undefined = automatic (collapsed, or open when the visitor is on a hidden link); true/false = the visitor's own choice
  const [openGroups, setOpenGroups] = useState<Record<string, boolean | undefined>>({});
  const setGroupOpen = (key: string) => (open: boolean) => setOpenGroups((g) => (g[key] === open ? g : { ...g, [key]: open }));
  // A new page starts from the automatic state again, so the active link is never left hidden.
  useEffect(() => {
    setOpenGroups({});
  }, [location.pathname]);

  // Contact form state
  const [contactName, setContactName] = useState<string>('');
  const [contactEmail, setContactEmail] = useState<string>('');
  const [contactSubject, setContactSubject] = useState<string>('General Feedback');
  const [contactMessage, setContactMessage] = useState<string>('');
  const [contactLoading, setContactLoading] = useState<boolean>(false);
  const [contactSuccess, setContactSuccess] = useState<string | null>(null);
  const [contactError, setContactError] = useState<string | null>(null);

  // Report issue form state
  const [issueTool, setIssueTool] = useState<string>('Quick Grade');
  const [issueTitle, setIssueTitle] = useState<string>('');
  const [issueDescription, setIssueDescription] = useState<string>('');
  const [issueEmail, setIssueEmail] = useState<string>('');
  const [issueLoading, setIssueLoading] = useState<boolean>(false);
  const [issueSuccess, setIssueSuccess] = useState<string | null>(null);
  const [issueError, setIssueError] = useState<string | null>(null);

  // Suggest feature form state
  const [featureName, setFeatureName] = useState<string>('');
  const [featureCategory, setFeatureCategory] = useState<string>('New Grading Scale');
  const [featureDescription, setFeatureDescription] = useState<string>('');
  const [featureEmail, setFeatureEmail] = useState<string>('');
  const [featureLoading, setFeatureLoading] = useState<boolean>(false);
  const [featureSuccess, setFeatureSuccess] = useState<string | null>(null);
  const [featureError, setFeatureError] = useState<string | null>(null);


  // WCAG 2.1 AA Compliant Unique IDs generated via React useId()
  const contactTitleId = useId();
  const contactErrorId = useId();
  const contactNameId = useId();
  const contactEmailId = useId();
  const contactSubjectId = useId();
  const contactMessageId = useId();

  const issueTitleHeaderId = useId();
  const issueErrorId = useId();
  const issueToolId = useId();
  const issueSummaryId = useId();
  const issueDescriptionId = useId();
  const issueEmailId = useId();

  const featureTitleHeaderId = useId();
  const featureErrorId = useId();
  const featureNameId = useId();
  const featureCategoryId = useId();
  const featureDescriptionId = useId();
  const featureEmailId = useId();

  const subscribeEmailId = useId();

  // Accessible focus trap, Escape key handling, and focus restoration for modal dialogs
  const modalRef = useRef<HTMLDivElement | null>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!activeModal) {
      if (triggerElementRef.current && typeof triggerElementRef.current.focus === 'function') {
        triggerElementRef.current.focus();
        triggerElementRef.current = null;
      }
      return;
    }

    // Modal is opened: remember trigger element to restore focus when closed
    triggerElementRef.current = document.activeElement as HTMLElement;

    // Focus first focusable interactive element inside the modal
    const focusTimer = window.setTimeout(() => {
      if (modalRef.current) {
        const focusableElements = Array.from(
          modalRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        );
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        } else {
          modalRef.current.focus();
        }
      }
    }, 50);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setActiveModal(null);
        return;
      }

      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = Array.from(
          modalRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0);

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement || !modalRef.current.contains(document.activeElement)) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement || !modalRef.current.contains(document.activeElement)) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeModal]);

  // API Base resolution (supports custom backend or default relative /api)
  const API_BASE = (
    (typeof import.meta !== 'undefined' && import.meta?.env?.VITE_API_URL) ||
    (typeof process !== 'undefined' && process?.env?.VITE_API_URL) ||
    ''
  ).replace(/\/+$/, '');

  const saveLocalOfflineSubmission = (key: string, item: any) => {
    try {
      if (typeof window === 'undefined') return;
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      existing.push({ ...item, timestamp: new Date().toISOString() });
      localStorage.setItem(key, JSON.stringify(existing));
    } catch (_) {}
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNav = (path: string, toolKey?: ToolKey) => {
    scrollToTop();
    if (toolKey && onSelectTool) {
      onSelectTool(toolKey);
    } else {
      navigate(path);
    }
  };

  // 1. Working Newsletter Subscribe button (connected to backend POST /api/subscribe)
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = subscribeEmail.trim();

    if (!clean) {
      setSubscribeMessage({ text: 'Please enter your email address to subscribe.', isError: true });
      if (setToast) setToast('Please enter your email address.');
      return;
    }

    if (!clean.includes('@') || !clean.includes('.')) {
      setSubscribeMessage({ text: 'Please enter a valid email format (e.g. name@school.edu).', isError: true });
      if (setToast) setToast('Please enter a valid email format.');
      return;
    }

    setSubscribeLoading(true);
    setSubscribeMessage(null);

    try {
      const result = await submitForm('subscribe', { email: clean }, API_BASE);
      if (result.ok) {
        setSubscribeMessage({ text: result.message || 'Subscribed! Thank you.', isError: false });
        setSubscribeEmail('');
        if (setToast) setToast('Subscribed to Easy Grade updates!');
      } else {
        setSubscribeMessage({ text: `We could not send that right now. Please email ${CONTACT_EMAIL} directly.`, isError: true });
        if (setToast) setToast('Could not subscribe right now.');
      }
    } finally {
      setSubscribeLoading(false);
    }
  };

  // 2. Working Contact Form button (connected to backend POST /api/contact)
  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactEmail.trim() || !contactMessage.trim()) {
      setContactError('Please enter both your email address and a message.');
      return;
    }

    setContactLoading(true);
    setContactError(null);
    setContactSuccess(null);

    const payload = {
      name: contactName,
      email: contactEmail,
      subject: contactSubject,
      message: contactMessage,
      category: contactSubject,
    };

    try {
      const result = await submitForm('contact', payload, API_BASE);
      if (result.ok) {
        setContactSuccess(result.message || 'Thank you! Your message was sent to our team.');
        setContactMessage('');
        if (setToast) setToast('Message sent successfully!');
        setTimeout(() => {
          setActiveModal(null);
          setContactSuccess(null);
        }, 2200);
      } else {
        setContactError(`We could not send that right now. Please email ${CONTACT_EMAIL} directly.`);
      }
    } finally {
      setContactLoading(false);
    }
  };

  // 3. Working Report Issue button (connected to backend POST /api/report-issue)
  const handleReportIssueSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!issueDescription.trim()) {
      setIssueError('Please provide details about the issue or calculation discrepancy.');
      return;
    }

    setIssueLoading(true);
    setIssueError(null);
    setIssueSuccess(null);

    const payload = {
      tool: issueTool,
      title: issueTitle || `${issueTool} Issue`,
      description: issueDescription,
      email: issueEmail,
    };

    try {
      const result = await submitForm('report-issue', payload, API_BASE);
      if (result.ok) {
        setIssueSuccess(result.message || 'Thank you! Your report was sent.');
        setIssueDescription('');
        setIssueTitle('');
        if (setToast) setToast('Bug report submitted!');
        setTimeout(() => {
          setActiveModal(null);
          setIssueSuccess(null);
        }, 2200);
      } else {
        setIssueError(`We could not send that right now. Please email ${CONTACT_EMAIL} directly.`);
      }
    } finally {
      setIssueLoading(false);
    }
  };

  // 4. Working Suggest Feature button (connected to backend POST /api/suggest-feature)
  const handleSuggestFeatureSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!featureName.trim() || !featureDescription.trim()) {
      setFeatureError('Please provide both a name and a description for your feature request.');
      return;
    }

    setFeatureLoading(true);
    setFeatureError(null);
    setFeatureSuccess(null);

    const payload = {
      featureName: featureName.trim(),
      category: featureCategory,
      description: featureDescription.trim(),
      email: featureEmail.trim(),
    };

    try {
      const result = await submitForm('suggest-feature', payload, API_BASE);
      if (result.ok) {
        setFeatureSuccess(result.message || 'Thank you! Your feature idea was sent.');
        setFeatureName('');
        setFeatureDescription('');
        if (setToast) setToast('Feature request sent!');
        setTimeout(() => {
          setActiveModal(null);
          setFeatureSuccess(null);
        }, 2200);
      } else {
        setFeatureError(`We could not send that right now. Please email ${CONTACT_EMAIL} directly.`);
      }
    } finally {
      setFeatureLoading(false);
    }
  };

  // 7. Working Copy Email button
  const handleCopyEmail = (e: React.MouseEvent) => {
    e.preventDefault();
    const email = CONTACT_EMAIL;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(email).then(() => {
        setCopiedEmail(true);
        if (setToast) setToast(`Email copied: ${email}`);
        setTimeout(() => setCopiedEmail(false), 2500);
      });
    }
  };

  // 8. Working Share link button
  const handleShareLink = (e: React.MouseEvent) => {
    e.preventDefault();
    const currentUrl = typeof window !== 'undefined' ? window.location.href : SITE_URL;

    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator
        .share({
          title: 'Easy Grade Tool',
          text: 'Calculate weighted grades, semester GPA, and needed final exam scores easily.',
          url: currentUrl,
        })
        .catch(() => {
          copyUrlFallback(currentUrl);
        });
    } else {
      copyUrlFallback(currentUrl);
    }
  };

  const copyUrlFallback = (url: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        setCopiedLink(true);
        if (setToast) setToast('Calculator link copied to clipboard!');
        setTimeout(() => setCopiedLink(false), 2500);
      });
    }
  };

  const handleOpenSearch = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onOpenCommandPalette) {
      onOpenCommandPalette();
    } else {
      window.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true, bubbles: true })
      );
    }
  };

  const toolIconMap: Record<ToolKey, React.ComponentType<{ className?: string }>> = {
    quick: GraduationCap,
    gpa: GraduationCap,
    cgpa: Calculator,
    percentage: Percent,
    tip: Lightbulb,
    loan: Banknote,
    mortgage: House,
    password: KeyRound,
  };

  // Shared footer styles (single place to tweak the look)
  const colHeadCls = 'text-base font-bold text-[#0F1114] m-0 mb-4';
  const linkCls =
    'inline-flex items-center min-h-[28px] text-left text-sm font-normal text-[#4F5D75] hover:text-[#2457F5] hover:underline underline-offset-4 transition-colors cursor-pointer bg-transparent border-0 p-0';
  const iconBtnCls =
    'size-11 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-full bg-[#EEF2FF] text-[#2457F5] hover:bg-[#2457F5] hover:text-white transition-colors cursor-pointer active:scale-95';

  return (
    <>
      <footer className="site-footer relative mt-16 mb-4 mx-3 sm:mx-6 rounded-[28px] overflow-hidden font-sans" aria-label="Site footer">
        <style dangerouslySetInnerHTML={{ __html: FOOTER_PREMIUM_CSS }} />
        {/* Newsletter band (Coursera-style pale-blue strip above the link columns). Backed by POST /api/subscribe */}
        <section aria-label="Newsletter" className="cs-news">
          <div className="cs-container">
          <div className="cs-news-card">
            <DecorLines variant="waves" className="cs-news-lines" />
            <DecorLines variant="arcs" className="cs-news-arcs" />
            <DecorLines variant="plane" className="cs-news-plane" />
          <div className="cs-news-inner">
            <div className="min-w-0">
              <h2 className="cs-news-title">Get updates on new grade formulas &amp; academic tools</h2>
              <p className="cs-news-sub">
                Free study updates when new grading scales, curve calculators and semester planning tools are added. No spam, unsubscribe anytime.
              </p>
            </div>
            <div className="min-w-0">
              <form onSubmit={handleSubscribe} noValidate className="cs-news-form">
                <label htmlFor={subscribeEmailId} className="sr-only">
                  Email address for updates
                </label>
                <input
                  id={subscribeEmailId}
                  type="email"
                  value={subscribeEmail}
                  onChange={(e) => {
                    setSubscribeEmail(e.target.value);
                    if (subscribeMessage) setSubscribeMessage(null);
                  }}
                  placeholder="Enter your email"
                  autoComplete="email"
                  className="cs-input min-w-0 flex-1"
                />
                <button type="submit" disabled={subscribeLoading} className="cs-btn cs-btn-primary">
                  {subscribeLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                      <span>Subscribing...</span>
                    </>
                  ) : (
                    <>
                      <span>Subscribe</span>
                      <ArrowRight className="w-4 h-4" aria-hidden="true" />
                    </>
                  )}
                </button>
              </form>
              <p className="cs-news-fine">
                By subscribing, you agree to our{' '}
                <Link to="/privacy" onClick={scrollToTop} className="underline underline-offset-2">
                  Privacy Policy
                </Link>
                .
              </p>
              {subscribeMessage && (
                <div
                  role="status"
                  className={`mt-3 max-w-md text-sm font-semibold px-4 py-2.5 rounded-2xl flex items-center gap-2 border ${
                    subscribeMessage.isError
                      ? 'bg-rose-50 text-rose-800 border-rose-200'
                      : 'bg-emerald-50 text-emerald-800 border-emerald-200'
                  }`}
                >
                  {subscribeMessage.isError ? (
                    <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
                  ) : (
                    <Check className="w-4 h-4 shrink-0" aria-hidden="true" />
                  )}
                  <span>{subscribeMessage.text}</span>
                </div>
              )}
            </div>
          </div>
          </div>
          </div>
        </section>

        {/* White footer panel */}
        <div className="cs-footer-panel relative pt-12 md:pt-14 transition-colors">
          <DecorLines variant="divider" className="cs-divider" />
          <div className="cs-container">
            {/* Journey band (Asana grouped directory, journey form): the same
                four-step flow as the homepage, so every page offers the path
                from scores to a plan — not just a list of links. */}
            <nav aria-label="From scores to a plan" className="nova2-foot-journey">
              <p className="nova2-foot-journey-title">From scores to a plan:</p>
              <Link to="/test-grade-calculator/"><span className="step-no" aria-hidden="true">1</span>Grade a test</Link>
              <Link to="/grading-scale/"><span className="step-no" aria-hidden="true">2</span>Check the scale</Link>
              <Link to="/gpa-calculator/"><span className="step-no" aria-hidden="true">3</span>Track the GPA</Link>
              <Link to="/final-grade-planner/"><span className="step-no" aria-hidden="true">4</span>Plan the finish</Link>
            </nav>
            <div className="grid grid-cols-2 md:grid-cols-12 gap-x-6 gap-y-10 pb-12">
              {/* Brand */}
              <div className="col-span-2 md:col-span-4 space-y-5">
                <Link
                  to="/"
                  onClick={() => {
                    scrollToTop();
                    if (onSelectTool) onSelectTool('quick');
                  }}
                  className="group inline-flex items-center text-left cursor-pointer transition-transform active:scale-95 focus:outline-none"
                  title="Go to Easy Grade Tool Home"
                >
                  <Logo size="md" />
                  {/* The logo is an SVG, so give the link real text for crawlers (invisible on screen). */}
                  <span className="sr-only">Easy Grade Tool Home</span>
                </Link>

                <p className="text-[13px] text-slate-500 leading-relaxed max-w-xs m-0">
                  An offline-first, private-by-design calculation suite built for students, educators, and everyday planning. Fast, accurate, and completely tracker-free.
                </p>

                {/* Quick action icons */}
                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={handleOpenSearch}
                    className={iconBtnCls}
                    title="Search calculators (Ctrl+K or ⌘K)"
                    aria-label="Open search command palette"
                  >
                    <Search className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={handleShareLink}
                    className={iconBtnCls}
                    title="Share current calculator or copy link"
                    aria-label="Share current calculator"
                  >
                    {copiedLink ? <Check className="w-4 h-4 text-emerald-600" aria-hidden="true" /> : <Share2 className="w-4 h-4" aria-hidden="true" />}
                  </button>
                  <button
                    type="button"
                    onClick={scrollToTop}
                    className={iconBtnCls}
                    title="Scroll back to top"
                    aria-label="Scroll back to top"
                  >
                    <ArrowUp className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              </div>

              {/* Calculators + More tools: original vertical lists, capped with a "More" button so every column is the same height */}
              {(['academic', 'utility'] as const).map((group) => {
                const isStaticPage =
                  location.pathname.startsWith('/about') ||
                  location.pathname.startsWith('/privacy') ||
                  location.pathname.startsWith('/terms') ||
                  location.pathname.startsWith('/methodology');
                const items = [
                  ...NAV_ITEMS.filter((t) => t.group === group).map((t) => ({
                    label: t.label,
                    title: t.title,
                    path: t.path,
                    toolKey: t.toolKey as ToolKey | null,
                  })),
                  ...(group === 'academic' ? FOOTER_EXTRA_ACADEMIC_LINKS.map((t) => ({ ...t, toolKey: null as ToolKey | null })) : []),
                ];
                const userChoice = openGroups[group];
                const hasMore = items.length > FOOTER_VISIBLE_LINKS;
                // Keep the expanded group open when the visitor is on one of its hidden pages.
                const hiddenActive = items.slice(FOOTER_VISIBLE_LINKS).some((t) => !isStaticPage && isSamePath(location.pathname, t.path));
                // An explicit click always wins; otherwise stay open only while the active page is a hidden link.
                const showAll = userChoice ?? hiddenActive;
                return (
                  <div key={group} className="md:col-span-2">
                    <h2 className={colHeadCls}>{group === 'academic' ? 'Grade Calculators' : 'More Tools'}</h2>
                    <ul className="space-y-3 list-none p-0 m-0">
                      {items.map((tool, idx) => {
                        const isActive = !isStaticPage && isSamePath(location.pathname, tool.path);
                        // Hidden items stay in the HTML (crawlable) but are not displayed until "More" is pressed.
                        const isHidden = idx >= FOOTER_VISIBLE_LINKS && !showAll;
                        return (
                          <li key={tool.path} className={isHidden ? 'hidden' : idx >= FOOTER_VISIBLE_LINKS ? 'ft-more-in' : undefined}>
                            <Link
                              to={tool.path}
                              onClick={() => {
                                scrollToTop();
                                // Skip for pages that share a tool key (e.g. Weighted/Final Exam) so we never jump back to Home.
                                if (tool.toolKey && isSamePath(TOOL_PATHS[tool.toolKey], tool.path)) onSelectTool?.(tool.toolKey);
                              }}
                              className={`${linkCls} no-underline ${isActive ? '!text-[#2457F5] font-bold' : ''}`}
                              aria-current={isActive ? 'page' : undefined}
                              title={tool.title}
                            >
                              {tool.label}
                            </Link>
                          </li>
                        );
                      })}
                      {hasMore && (
                        <li>
                          <button
                            type="button"
                            onClick={() => setGroupOpen(group)(!showAll)}
                            aria-expanded={showAll}
                            className="ft-more-btn"
                          >
                            <span>{showAll ? 'Show less' : `More (${items.length - FOOTER_VISIBLE_LINKS})`}</span>
                            <ChevronDown className={`ft-more-chev w-3.5 h-3.5 ${showAll ? 'rotate-180' : ''}`} aria-hidden="true" />
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                );
              })}

              {/* Support */}
              <div className="md:col-span-2">
                <h2 className={colHeadCls}>Support</h2>
                <ul className="space-y-3 list-none p-0 m-0">
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        setIssueSuccess(null);
                        setIssueError(null);
                        setActiveModal('reportIssue');
                      }}
                      className={linkCls}
                      title="Report a calculation error or UI bug directly to backend ticket system"
                    >
                      Report an Issue
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        setFeatureSuccess(null);
                        setFeatureError(null);
                        setActiveModal('suggestFeature');
                      }}
                      className={linkCls}
                      title="Suggest a new grading scale, formula or calculator to the roadmap"
                    >
                      Request a Feature
                    </button>
                  </li>
                </ul>
              </div>

              {/* Contact Us */}
              <div className="md:col-span-2">
                <h2 className={colHeadCls}>Contact Us</h2>
                <ul className="space-y-3.5 list-none p-0 m-0">
                  <li>
                    <button
                      type="button"
                      onClick={handleCopyEmail}
                      className={`${linkCls} gap-2.5`}
                      title={`Copy direct email address: ${CONTACT_EMAIL}`}
                    >
                      <span className="w-6 h-6 rounded-md bg-[#2457F5] text-white flex items-center justify-center shrink-0">
                        {copiedEmail ? <Check className="w-3.5 h-3.5" aria-hidden="true" /> : <Mail className="w-3.5 h-3.5" aria-hidden="true" />}
                      </span>
                      <span className="break-all">{copiedEmail ? 'Copied!' : CONTACT_EMAIL}</span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        setContactSuccess(null);
                        setContactError(null);
                        setActiveModal('contact');
                      }}
                      className={`${linkCls} gap-2.5`}
                      title="Send a message or feedback directly to the engineering team via backend"
                    >
                      <span className="w-6 h-6 rounded-md bg-[#2457F5] text-white flex items-center justify-center shrink-0">
                        <MessageSquare className="w-3.5 h-3.5" aria-hidden="true" />
                      </span>
                      <span>Contact &amp; Feedback</span>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="cs-footer-bottom">
            <div className="cs-container py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500 ">
              <p className="m-0 text-center md:text-left">
                &copy; {currentYear} Easy Grade Tool. Free client-side calculation suite. Engineered with{' '}
                <Heart className="w-3 h-3 text-rose-500 fill-rose-500 inline -mt-0.5" aria-hidden="true" /> for students and educators.
              </p>
              <nav aria-label="Legal" className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-medium text-slate-600 ">
                <Link
                  to="/about"
                  onClick={scrollToTop}
                  onMouseEnter={() => preloadTool('about')}
                  onFocus={() => preloadTool('about')}
                  onTouchStart={() => preloadTool('about')}
                  className="no-underline hover:text-[#2457F5] transition-colors"
                  title="Learn about our grading methodology, GPA formulas, and project mission"
                >
                  About &amp; Methodology
                </Link>
                <Link to="/privacy" onClick={scrollToTop} className="no-underline hover:text-[#2457F5] transition-colors" title="Read our Privacy Policy">
                  Privacy Policy
                </Link>
                <Link to="/terms" onClick={scrollToTop} className="no-underline hover:text-[#2457F5] transition-colors" title="View Terms of Service & Educational Disclaimer">
                  Terms of Service
                </Link>
              </nav>
            </div>
          </div>
        </div>
      </footer>

      {/* ========================================================================= */}
      {/* MODAL 1: CONTACT & FEEDBACK (Submits to POST /api/contact) */}
      {/* ========================================================================= */}
      {activeModal === 'contact' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0E1424]/35 backdrop-blur-xs animate-in fade-in duration-150"
          onClick={(e) => {
            if (e.target === e.currentTarget) setActiveModal(null);
          }}
        >
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={contactTitleId}
            tabIndex={-1}
            className="relative w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden p-6 sm:p-7 outline-none"
          >
            <button
              type="button"
              onClick={() => setActiveModal(null)}
              className="absolute top-5 right-5 p-1 rounded-full text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-teal-100 text-teal-600 flex items-center justify-center shrink-0">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div>
                <h3 id={contactTitleId} className="text-base font-bold text-slate-900 ">Contact &amp; Feedback</h3>
                <p className="text-xs text-slate-500 ">
                  Send questions, formula inquiries, or feedback directly to our team at{' '}
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="font-medium text-teal-600 underline underline-offset-2 hover:text-teal-700 "
                  >
                    {CONTACT_EMAIL}
                  </a>.
                </p>
              </div>
            </div>

            {contactSuccess ? (
              <div className="py-8 text-center space-y-3">
                <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h4 className="text-sm font-bold text-slate-900 ">Message Sent!</h4>
                <p className="text-xs text-slate-600 max-w-sm mx-auto">{contactSuccess}</p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-4">
                {contactError && (
                  <div
                    id={contactErrorId}
                    role="alert"
                    className="p-3 text-xs text-rose-700 bg-rose-50 rounded-xl border border-rose-200 flex items-center gap-2"
                  >
                    <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
                    <span>{contactError}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor={contactNameId} className="block text-xs font-semibold text-slate-700 mb-1">
                      Your Name
                    </label>
                    <input
                      id={contactNameId}
                      type="text"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      placeholder="e.g. Alex Chen"
                      className="w-full min-h-[44px] px-3.5 py-2.5 text-base sm:text-xs rounded-xl bg-white border border-slate-200/90 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/15 hover:border-slate-300 transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor={contactEmailId} className="block text-xs font-semibold text-slate-700 mb-1">
                      Email Address <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id={contactEmailId}
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="your.email@school.edu"
                      required
                      aria-required="true"
                      aria-describedby={contactError ? contactErrorId : undefined}
                      className="w-full min-h-[44px] px-3.5 py-2.5 text-base sm:text-xs rounded-xl bg-white border border-slate-200/90 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/15 hover:border-slate-300 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor={contactSubjectId} className="block text-xs font-semibold text-slate-700 mb-1">
                    Subject / Category
                  </label>
                  <select
                    id={contactSubjectId}
                    value={contactSubject}
                    onChange={(e) => setContactSubject(e.target.value)}
                    className="w-full min-h-[44px] px-3.5 py-2.5 text-base sm:text-xs rounded-xl bg-white border border-slate-200/90 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/15 hover:border-slate-300 transition-all"
                  >
                    <option value="General Feedback">General Feedback</option>
                    <option value="Grading Formula Question">Grading Formula Question</option>
                    <option value="GPA Scale Accuracy">GPA Scale Accuracy</option>
                    <option value="Academic Institution Inquiry">Academic Institution Inquiry</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor={contactMessageId} className="block text-xs font-semibold text-slate-700 mb-1">
                    Your Message <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    id={contactMessageId}
                    rows={4}
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    placeholder="Tell us what you love or how we can make Easy Grade better for you..."
                    required
                    aria-required="true"
                    aria-describedby={contactError ? contactErrorId : undefined}
                    className="w-full px-3.5 py-2.5 text-base sm:text-xs rounded-xl bg-white border border-slate-200/90 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/15 hover:border-slate-300 transition-all resize-none"
                  />
                </div>

                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="text-xs font-mono text-slate-500 hover:text-slate-800 transition-colors flex items-center gap-1 cursor-pointer"
                    title={`Copy direct email address: ${CONTACT_EMAIL}`}
                  >
                    <Mail className="w-3.5 h-3.5" />
                    <span>Copy email ({CONTACT_EMAIL})</span>
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setActiveModal(null)}
                      className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-full cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={contactLoading}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-teal-700 hover:bg-teal-800 rounded-full transition-all cursor-pointer shadow-xs disabled:opacity-60"
                    >
                      {contactLoading ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-3.5 h-3.5" />
                          <span>Send Message</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 2: REPORT ISSUE / BUG (Submits to POST /api/report-issue) */}
      {/* ========================================================================= */}
      {activeModal === 'reportIssue' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0E1424]/35 backdrop-blur-xs animate-in fade-in duration-150"
          onClick={(e) => {
            if (e.target === e.currentTarget) setActiveModal(null);
          }}
        >
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={issueTitleHeaderId}
            tabIndex={-1}
            className="relative w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden p-6 sm:p-7 outline-none"
          >
            <button
              type="button"
              onClick={() => setActiveModal(null)}
              className="absolute top-5 right-5 p-1 rounded-full text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                <Bug className="w-5 h-5" />
              </div>
              <div>
                <h3 id={issueTitleHeaderId} className="text-base font-bold text-slate-900 ">Report an Issue</h3>
                <p className="text-xs text-slate-500 ">
                  Encountered a calculation rounding issue or broken feature? Let us know.
                </p>
              </div>
            </div>

            {issueSuccess ? (
              <div className="py-8 text-center space-y-3">
                <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h4 className="text-sm font-bold text-slate-900 ">Issue Logged!</h4>
                <p className="text-xs text-slate-600 max-w-sm mx-auto">{issueSuccess}</p>
              </div>
            ) : (
              <form onSubmit={handleReportIssueSubmit} className="space-y-4">
                {issueError && (
                  <div
                    id={issueErrorId}
                    role="alert"
                    className="p-3 text-xs text-rose-700 bg-rose-50 rounded-xl border border-rose-200 flex items-center gap-2"
                  >
                    <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
                    <span>{issueError}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor={issueToolId} className="block text-xs font-semibold text-slate-700 mb-1">
                      Affected Calculator
                    </label>
                    <select
                      id={issueToolId}
                      value={issueTool}
                      onChange={(e) => setIssueTool(e.target.value)}
                      className="w-full min-h-[44px] px-3.5 py-2.5 text-base sm:text-xs rounded-xl bg-white border border-slate-200/90 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] focus:outline-none focus:border-teal-600 focus:ring-4 focus:ring-teal-500/15 hover:border-slate-300 transition-all"
                    >
                      <option value="Quick Grade">Quick Grade Calculator</option>
                      <option value="GPA Calculator">GPA Calculator</option>
                      <option value="CGPA to Percentage Calculator">CGPA to Percentage Calculator</option>
                      <option value="Percentage Calculator">Percentage Calculator</option>
                      <option value="Tip Calculator">Tip Calculator</option>
                      <option value="Loan Calculator">Loan Calculator</option>
                      <option value="Mortgage Calculator">Mortgage Calculator</option>
                      <option value="Password Generator">Password Generator</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor={issueSummaryId} className="block text-xs font-semibold text-slate-700 mb-1">
                      Issue Summary
                    </label>
                    <input
                      id={issueSummaryId}
                      type="text"
                      value={issueTitle}
                      onChange={(e) => setIssueTitle(e.target.value)}
                      placeholder="e.g. Weighted curve rounding discrepancy"
                      className="w-full min-h-[44px] px-3.5 py-2.5 text-base sm:text-xs rounded-xl bg-white border border-slate-200/90 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] focus:outline-none focus:border-teal-600 focus:ring-4 focus:ring-teal-500/15 hover:border-slate-300 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor={issueDescriptionId} className="block text-xs font-semibold text-slate-700 mb-1">
                    Details &amp; Steps to Reproduce <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    id={issueDescriptionId}
                    rows={4}
                    value={issueDescription}
                    onChange={(e) => setIssueDescription(e.target.value)}
                    placeholder="Describe what values you entered, what result you saw, and what you expected..."
                    required
                    aria-required="true"
                    aria-describedby={issueError ? issueErrorId : undefined}
                    className="w-full px-3.5 py-2.5 text-base sm:text-xs rounded-xl bg-white border border-slate-200/90 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] focus:outline-none focus:border-teal-600 focus:ring-4 focus:ring-teal-500/15 hover:border-slate-300 transition-all resize-none"
                  />
                </div>

                <div>
                  <label htmlFor={issueEmailId} className="block text-xs font-semibold text-slate-700 mb-1">
                    Your Email (optional, for follow-up)
                  </label>
                  <input
                    id={issueEmailId}
                    type="email"
                    value={issueEmail}
                    onChange={(e) => setIssueEmail(e.target.value)}
                    placeholder="student@university.edu"
                    className="w-full min-h-[44px] px-3.5 py-2.5 text-base sm:text-xs rounded-xl bg-white border border-slate-200/90 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] focus:outline-none focus:border-teal-600 focus:ring-4 focus:ring-teal-500/15 hover:border-slate-300 transition-all"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setActiveModal(null)}
                    className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-full cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={issueLoading}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-teal-700 hover:bg-teal-800 rounded-full transition-all cursor-pointer shadow-xs disabled:opacity-60"
                  >
                    {issueLoading ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Logging...</span>
                      </>
                    ) : (
                      <>
                        <Bug className="w-3.5 h-3.5" />
                        <span>Submit Ticket</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 3: REQUEST A FEATURE (Submits to POST /api/suggest-feature) */}
      {/* ========================================================================= */}
      {activeModal === 'suggestFeature' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0E1424]/35 backdrop-blur-xs animate-in fade-in duration-150"
          onClick={(e) => {
            if (e.target === e.currentTarget) setActiveModal(null);
          }}
        >
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={featureTitleHeaderId}
            tabIndex={-1}
            className="relative w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden p-6 sm:p-7 outline-none"
          >
            <button
              type="button"
              onClick={() => setActiveModal(null)}
              className="absolute top-5 right-5 p-1 rounded-full text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                <PlusCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 id={featureTitleHeaderId} className="text-base font-bold text-slate-900 ">Request a Calculator or Feature</h3>
                <p className="text-xs text-slate-500 ">
                  Suggest a new grading scale, formula system, or calculator type.
                </p>
              </div>
            </div>

            {featureSuccess ? (
              <div className="py-8 text-center space-y-3">
                <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h4 className="text-sm font-bold text-slate-900 ">Feature Submitted!</h4>
                <p className="text-xs text-slate-600 max-w-sm mx-auto">{featureSuccess}</p>
              </div>
            ) : (
              <form onSubmit={handleSuggestFeatureSubmit} className="space-y-4">
                {featureError && (
                  <div
                    id={featureErrorId}
                    role="alert"
                    className="p-3 text-xs text-rose-700 bg-rose-50 rounded-xl border border-rose-200 flex items-center gap-2"
                  >
                    <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
                    <span>{featureError}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor={featureNameId} className="block text-xs font-semibold text-slate-700 mb-1">
                      Feature / Tool Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id={featureNameId}
                      type="text"
                      value={featureName}
                      onChange={(e) => setFeatureName(e.target.value)}
                      placeholder="e.g. Canadian 9.0 GPA Scale"
                      required
                      aria-required="true"
                      aria-describedby={featureError ? featureErrorId : undefined}
                      className="w-full min-h-[44px] px-3.5 py-2.5 text-base sm:text-xs rounded-xl bg-white border border-slate-200/90 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] focus:outline-none focus:border-teal-600 focus:ring-4 focus:ring-teal-500/15 hover:border-slate-300 transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor={featureCategoryId} className="block text-xs font-semibold text-slate-700 mb-1">
                      Category
                    </label>
                    <select
                      id={featureCategoryId}
                      value={featureCategory}
                      onChange={(e) => setFeatureCategory(e.target.value)}
                      className="w-full min-h-[44px] px-3.5 py-2.5 text-base sm:text-xs rounded-xl bg-white border border-slate-200/90 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] focus:outline-none focus:border-teal-600 focus:ring-4 focus:ring-teal-500/15 hover:border-slate-300 transition-all"
                    >
                      <option value="New Grading Scale">New Grading Scale</option>
                      <option value="New Calculator Type">New Calculator Type</option>
                      <option value="Formula Enhancement">Formula Enhancement</option>
                      <option value="Export & Reporting Option">Export &amp; Reporting Option</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor={featureDescriptionId} className="block text-xs font-semibold text-slate-700 mb-1">
                    Description &amp; Why It&apos;s Useful <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    id={featureDescriptionId}
                    rows={4}
                    value={featureDescription}
                    onChange={(e) => setFeatureDescription(e.target.value)}
                    placeholder="Describe how the calculator should work or provide links to standard formulas..."
                    required
                    aria-required="true"
                    aria-describedby={featureError ? featureErrorId : undefined}
                    className="w-full px-3.5 py-2.5 text-base sm:text-xs rounded-xl bg-white border border-slate-200/90 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] focus:outline-none focus:border-teal-600 focus:ring-4 focus:ring-teal-500/15 hover:border-slate-300 transition-all resize-none"
                  />
                </div>

                <div>
                  <label htmlFor={featureEmailId} className="block text-xs font-semibold text-slate-700 mb-1">
                    Your Email (optional, for roadmap notification)
                  </label>
                  <input
                    id={featureEmailId}
                    type="email"
                    value={featureEmail}
                    onChange={(e) => setFeatureEmail(e.target.value)}
                    placeholder="you@school.edu"
                    className="w-full min-h-[44px] px-3.5 py-2.5 text-base sm:text-xs rounded-xl bg-white border border-slate-200/90 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.9)] focus:outline-none focus:border-teal-600 focus:ring-4 focus:ring-teal-500/15 hover:border-slate-300 transition-all"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setActiveModal(null)}
                    className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-full cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={featureLoading}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-teal-700 hover:bg-teal-800 rounded-full transition-all cursor-pointer shadow-xs disabled:opacity-60"
                  >
                    {featureLoading ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>Submit Idea</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

    </>
  );
};
